function presenceColor(userId) {
    const palette = ['#7c3aed', '#db2777', '#059669', '#d97706', '#2563eb', '#dc2626'];
    const source = String(userId || '0');
    let hash = 0;
    for (let index = 0; index < source.length; index += 1) hash = ((hash * 31) + source.charCodeAt(index)) >>> 0;
    return palette[hash % palette.length];
}

function otherParticipants(participants, selfUserId) {
    const users = new Map();
    (participants || []).forEach(actor => {
        const id = Number(actor && actor.user_id);
        if (!Number.isInteger(id) || id <= 0 || id === Number(selfUserId)) return;
        users.set(id, {...actor, user_id: id, color: presenceColor(id)});
    });
    return Array.from(users.values()).sort((a, b) => a.user_id - b.user_id);
}

function createCollaborationClient({socket, onChange, timers, clientId, isActive = () => true}) {
    let context = null;
    let watchId = null;
    let sequence = 0;
    let joined = false;
    let pending = false;
    let forbidden = false;
    let selfUserId = null;
    let heartbeat = null;
    let destroyed = false;
    let reportedActive = null;

    function matches(payload) {
        return context && payload && payload.scope === context.scope
            && Number(payload.resource_id || 0) === Number(context.resource_id || 0);
    }
    function eligible() { return !destroyed && context && context.ready && socket.connected; }
    function publish(payload) { onChange({participants: payload && payload.participants || [], self_user_id: selfUserId}); }
    function stop() {
        const previous = watchId;
        watchId = null;
        sequence += 1;
        joined = false;
        pending = false;
        selfUserId = null;
        if (heartbeat !== null) timers.clearInterval(heartbeat);
        heartbeat = null;
        publish(null);
        if (previous && socket.connected) socket.emit('collaboration:unwatch', {watch_id: previous});
    }
    function join() {
        if (!eligible() || pending || forbidden || !isActive()) return;
        const current = ++sequence;
        watchId = `${clientId}:${current}`;
        pending = true;
        reportedActive = isActive();
        socket.timeout(10000).emit('collaboration:watch', {
            scope: context.scope, resource_id: context.resource_id || null, watch_id: watchId, active: reportedActive
        }, (error, response) => {
            if (current !== sequence || !eligible()) return;
            pending = false;
            if (error || !response || !response.ok || !matches(response)) {
                joined = false;
                forbidden = Boolean(response && response.code === 'COLLABORATION_FORBIDDEN');
                publish(null);
                return;
            }
            selfUserId = response.self_user_id;
            joined = true;
            publish(response);
        });
    }
    function tick() {
        if (!eligible()) { stop(); return; }
        if (!joined) { join(); return; }
        const current = sequence;
        reportedActive = isActive();
        socket.timeout(10000).emit('collaboration:heartbeat', {watch_id: watchId, active: reportedActive}, (error, response) => {
            if (current !== sequence || !eligible()) return;
            if (!error && response && response.ok) return;
            joined = false;
            forbidden = Boolean(response && response.code === 'COLLABORATION_FORBIDDEN');
            publish(null);
            if (!forbidden) join();
        });
    }
    function refresh() {
        stop();
        forbidden = false;
        if (!eligible()) return;
        heartbeat = timers.setInterval(tick, 15000);
        join();
    }
    function onPresence(payload) {
        if (joined && eligible() && matches(payload)) publish(payload);
    }
    socket.on('collaboration:presence', onPresence);
    socket.on('connect', refresh);
    socket.on('disconnect', stop);
    return {
        update(value) { context = value; refresh(); },
        visibilityChanged() { forbidden = false; tick(); },
        activityChanged() { if (eligible() && isActive() !== reportedActive) tick(); },
        destroy() {
            destroyed = true;
            stop();
            socket.off('collaboration:presence', onPresence);
            socket.off('connect', refresh);
            socket.off('disconnect', stop);
        }
    };
}

function activeEditorForUser(editors, userId, now = Date.now()) {
    return Object.values(editors || {}).filter(editor => (
        Number(editor.actor && editor.actor.user_id) === Number(userId)
        && Number(editor.seen_at) >= now - 20000
        && (editor.cell_key || (editor.ranges && editor.ranges.length))
    )).sort((a, b) => Number(b.seen_at) - Number(a.seen_at))[0] || null;
}

function parsePresenceCellKey(key) {
    const entry = /^entry:([1-9]\d*):(\d{4}-\d{2}-\d{2})$/.exec(key || '');
    if (entry) return {lineId: Number(entry[1]), date: entry[2]};
    const detail = /^detail:([1-9]\d*):(ktu|characteristic:.+)$/.exec(key || '');
    return detail ? {lineId: Number(detail[1]), field: detail[2]} : null;
}

function revealCollaborationCell(element, color, scroll = true) {
    if (!element) return false;
    if (scroll && element.scrollIntoView) element.scrollIntoView({block: 'center', inline: 'center', behavior: 'smooth'});
    if (element.animate) element.animate([
        {outline: `3px solid ${color}`, outlineOffset: '-3px'},
        {outline: '3px solid transparent', outlineOffset: '-3px'}
    ], {duration: 1200, iterations: 2});
    return true;
}

module.exports = {presenceColor, otherParticipants, createCollaborationClient,
    activeEditorForUser, parsePresenceCellKey, revealCollaborationCell};
